import type { GalleryPost } from "@/shared/lib/supabase/mappers"

export function normalizeImportSourceId(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim()
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return null
}

export function normalizeGalleryMediaKey(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ""

  try {
    const parsed = new URL(trimmed)
    let path = parsed.pathname
    path = path.replace(/\/(full|thumb)\.(jpe?g|png|webp|gif)$/i, "")
    path = path.replace(/\/+$/, "")
    return `${parsed.hostname}${path}`.toLowerCase()
  } catch {
    return trimmed
      .split("?")[0]
      .replace(/\/(full|thumb)\.(jpe?g|png|webp|gif)$/i, "")
      .toLowerCase()
  }
}

type GalleryRowLike = {
  id: string
  media_url?: string | null
  thumbnail_url?: string | null
  generation_params?: unknown
}

function getGalleryImportOrMediaDedupeKey(row: GalleryRowLike): string {
  const params = row.generation_params as { import_source_id?: unknown } | null
  const sourceId = normalizeImportSourceId(params?.import_source_id)
  if (sourceId) return `source:${sourceId}`

  const mediaKeys = [row.media_url, row.thumbnail_url]
    .filter((url): url is string => Boolean(url?.trim()))
    .map(normalizeGalleryMediaKey)
    .filter(Boolean)
    .sort()

  if (mediaKeys.length > 0) {
    return `media:${mediaKeys[0]}`
  }

  return `post:${row.id}`
}

export function getGalleryDuplicateCleanupKey(row: GalleryRowLike): string {
  return getGalleryImportOrMediaDedupeKey(row)
}

export function getGalleryRowDedupeKey(row: GalleryRowLike): string {
  const params = row.generation_params as {
    generation_id?: unknown
    import_source_id?: unknown
  } | null

  const generationId =
    typeof params?.generation_id === "string" ? params.generation_id.trim() : ""
  if (generationId) return `generation:${generationId}`

  return getGalleryImportOrMediaDedupeKey(row)
}

export function getGalleryPostDedupeKey(post: GalleryPost): string {
  const generationId = post.generationParams?.generation_id?.trim()
  if (generationId) return `generation:${generationId}`

  const sourceId = normalizeImportSourceId(post.generationParams?.import_source_id)
  if (sourceId) return `source:${sourceId}`

  const mediaKeys = [post.mediaUrl, post.thumbnailUrl]
    .filter((url): url is string => Boolean(url?.trim()))
    .map(normalizeGalleryMediaKey)
    .filter(Boolean)
    .sort()

  if (mediaKeys.length > 0) {
    return `media:${mediaKeys[0]}`
  }

  return `post:${post.id}`
}

export function dedupeGalleryPosts(posts: GalleryPost[]): GalleryPost[] {
  const seen = new Set<string>()
  const deduped: GalleryPost[] = []

  posts.forEach((post) => {
    const key = getGalleryPostDedupeKey(post)
    if (seen.has(key)) return
    seen.add(key)
    deduped.push(post)
  })

  return deduped
}
